import { createTab } from './factories'
import { Tab, TabType } from './types'

// =============================================================================
// COMPACT FORMAT TYPES
// =============================================================================

/**
 * Dynamic, dictionary-based compact format
 * d: payload keys dictionary (excludes id/title/type)
 * tt: tab type dictionary
 * l/r: arrays of vectors where first element is type index, followed by payload values aligned to d
 * a: active indices [leftIndex, rightIndex]
 * e: enable split view flag
 */
export type CompactSplitViewState = {
  d: string[]
  tt: string[]
  l: _Vector[]
  r?: _Vector[]
  a: [number | null, number | null]
  e?: 1
}

type _Dictionaries = { keys: string[]; types: string[] }
type _Vector = (string | null | number)[]

// =============================================================================
// SERIALIZATION FUNCTIONS
// =============================================================================

/**
 * Build dictionaries for keys and types from tab arrays
 */
const _buildDictionaries = (left: Tab[], right: Tab[]): _Dictionaries => {
  const all = [...left, ...right]
  const keysSet = new Set<string>()
  const typesSet = new Set<string>()

  for (const tab of all) {
    typesSet.add(tab.type)
    for (const key of Object.keys(tab)) {
      if (key !== 'id' && key !== 'type') {
        keysSet.add(key)
      }
    }
  }

  return {
    keys: Array.from(keysSet),
    types: Array.from(typesSet),
  }
}

/**
 * Convert a tab to a compact vector representation
 */
const _tabToVector = (tab: Tab, dicts: _Dictionaries): _Vector => {
  const typeIndex = dicts.types.indexOf(tab.type)
  const values: Array<string | null> = dicts.keys.map((k) => tab[k as keyof Tab] ?? null)
  return [typeIndex, ...values]
}

/**
 * Convert a compact vector back to a tab
 */
const _vectorToTab = (vec: _Vector, dicts: _Dictionaries): Tab => {
  const typeIdx = (vec[0] as number) ?? 0
  const type = dicts.types[typeIdx] as TabType
  const payload: Record<string, string> = {}

  for (let i = 0; i < dicts.keys.length; i++) {
    const value = vec[i + 1]
    if (value != null) {
      payload[dicts.keys[i]] = String(value)
    }
  }

  return createTab({ type, ...payload })
}

// =============================================================================
// PARSER IMPLEMENTATION
// =============================================================================

/**
 * Parse compact URL state back to full split view state
 */
export const parseCompactState = (
  str: string,
): {
  tabsLeft: Tab[]
  tabsRight: Tab[]
  activeTabIdLeft: string | null
  activeTabIdRight: string | null
  enableSplitView: boolean
} | null => {
  try {
    const compact = JSON.parse(decodeURIComponent(str)) as CompactSplitViewState

    if (!compact || !Array.isArray(compact.l)) {
      throw new Error('Invalid split view state')
    }

    const dicts: _Dictionaries = { keys: compact.d || [], types: compact.tt || [] }
    const tabsLeft = compact.l.map((v) => _vectorToTab(v, dicts))
    const tabsRight = (compact.r || []).map((v) => _vectorToTab(v, dicts))

    const activeLeft =
      typeof compact.a?.[0] === 'number' && compact.a[0] != null && tabsLeft[compact.a[0]]
        ? tabsLeft[compact.a[0]].id
        : tabsLeft.length > 0
          ? tabsLeft[0].id
          : null

    const activeRight =
      typeof compact.a?.[1] === 'number' && compact.a[1] != null && tabsRight[compact.a[1]]
        ? tabsRight[compact.a[1]].id
        : tabsRight.length > 0
          ? tabsRight[0].id
          : null

    return {
      tabsLeft,
      tabsRight,
      activeTabIdLeft: activeLeft,
      activeTabIdRight: activeRight,
      enableSplitView: compact.e === 1,
    }
  } catch {
    return null
  }
}

/**
 * Serialize full split view state to compact URL format
 */
export const serializeToCompactState = (state: {
  tabsLeft: Tab[]
  tabsRight: Tab[]
  activeTabIdLeft: string | null
  activeTabIdRight: string | null
  enableSplitView: boolean
}): string => {
  const dicts = _buildDictionaries(state.tabsLeft, state.tabsRight)
  const l = state.tabsLeft.map((t) => _tabToVector(t, dicts))
  const r =
    state.tabsRight.length > 0 ? state.tabsRight.map((t) => _tabToVector(t, dicts)) : undefined

  const leftActiveIndex = state.activeTabIdLeft
    ? state.tabsLeft.findIndex((t) => t.id === state.activeTabIdLeft)
    : null
  const rightActiveIndex = state.activeTabIdRight
    ? state.tabsRight.findIndex((t) => t.id === state.activeTabIdRight)
    : null

  const compact: CompactSplitViewState = {
    d: dicts.keys,
    tt: dicts.types,
    l,
    a: [
      leftActiveIndex !== -1 ? leftActiveIndex : null,
      rightActiveIndex !== -1 ? rightActiveIndex : null,
    ],
  }

  if (state.enableSplitView) compact.e = 1
  if (r && r.length > 0) compact.r = r

  return encodeURIComponent(JSON.stringify(compact))
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get the size reduction ratio of the compact format
 */
export const getCompressionRatio = (original: string, compact: string): number => {
  return 1 - compact.length / original.length
}

/**
 * Validate that a compact state can be parsed
 */
export const isValidCompactState = (str: string): boolean => {
  try {
    const parsed = parseCompactState(str)
    return parsed !== null
  } catch {
    return false
  }
}
